import { Flex, Table, Typography } from 'antd';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { LibraryContext } from './context-provider';
const { Text } = Typography;

export default function LibraryTable({ library }) {
   const [selectedRowKeys, setSelectedRowKeys] = useState([]);
   const { selectedLibrary, setSelectedLibrary } = useContext(LibraryContext);

   // Set selected row keys when selectedLibrary changes
   useEffect(() => {
      if (selectedLibrary) {
         const selectedKeys = selectedLibrary?.map((item) => item.type + item._id);
         setSelectedRowKeys(selectedKeys);
      }
   }, [selectedLibrary, library]);

   // merge file and folder data into one array and add type property
   const libraryData = library?.data?.files
      ?.map((file) => {
         return {
            type: 'file',
            ...file,
         };
      })
      .concat(
         library?.data?.folders?.map((folder) => {
            return {
               type: 'folder',
               ...folder,
            };
         })
      );

   // Table columns
   const columns = [
      {
         title: 'Name',
         dataIndex: 'name',
         key: 'name',
         render: (text, record) => (
            <Flex gap={16} align="center">
               {record.type === 'file' && <i className="fi fi-rr-file" style={{ fontSize: 16 }} />}
               {record.type === 'folder' && (
                  <i className="fi fi-sr-folder" style={{ fontSize: 16, color: record.color }} />
               )}
               <a>{text}</a>
            </Flex>
         ),
      },
      {
         title: 'Created At',
         dataIndex: 'createdAt',
         key: 'createdAt',
         render: (text) => <Text type="secondary">{moment(text).format('DD MMM YYYY')}</Text>,
      },
      {
         title: 'Size',
         dataIndex: ['file', 'size'],
         key: 'size',
         render: (text) => {
            if (!text) return null;
            // Convert bytes to KB, MB, GB
            const bytes = text;
            const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
            if (bytes === 0) return '0 Byte';
            const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
            return <Text type="secondary">{parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i]}</Text>;
         },
      },
   ];

   function handleRowClick(record) {
      record.key = record.type + record._id;

      // if key pressed is ctrl or cmd
      if (window.event.ctrlKey || window.event.metaKey) {
         // if row is already selected, deselect it
         if (selectedRowKeys.includes(record.key)) {
            setSelectedRowKeys(selectedRowKeys.filter((key) => key !== record.key));
            setSelectedLibrary(selectedLibrary.filter((row) => row.key !== record.key));
         }
         // else select it
         else {
            setSelectedRowKeys([...selectedRowKeys, record.key]);
            setSelectedLibrary([...selectedLibrary, record]);
         }
      }
      // if key pressed is shift
      else if (window.event.shiftKey) {
         // disable default text selection
         window.getSelection().removeAllRanges();
         // if there is no row selected, select it
         if (selectedRowKeys.length === 0) {
            setSelectedRowKeys([record.key]);
            setSelectedLibrary([record]);
         }
         // else select all rows between the first selected row and the current row
         else {
            const firstSelectedRowIndex = libraryData.findIndex((row) => row.type + row._id === selectedRowKeys[0]);
            const currentRowIndex = libraryData.findIndex((row) => row.type + row._id === record.key);
            const rowsToSelect = libraryData.slice(
               Math.min(firstSelectedRowIndex, currentRowIndex),
               Math.max(firstSelectedRowIndex, currentRowIndex) + 1
            );
            setSelectedRowKeys(rowsToSelect.map((row) => row.type + row._id));
            setSelectedLibrary(rowsToSelect);
         }
      }

      // if no key is pressed, select only the current row
      else {
         setSelectedRowKeys([record.key]);
         setSelectedLibrary([record]);
      }
   }

   return (
      <>
         <Table
            size="small"
            rowKey={(record) => record.type + record._id}
            onRow={(record) => {
               return {
                  onClick: () => {
                     handleRowClick(record);
                  },
                  style: { cursor: 'pointer' },
               };
            }}
            rowSelection={{
               type: 'checkbox',
               selectedRowKeys: selectedRowKeys,
               onChange: (selectedRowKeys, selectedRows) => {
                  setSelectedRowKeys(selectedRowKeys);
                  setSelectedLibrary(selectedRows);
               },
            }}
            columns={columns}
            dataSource={libraryData}
            pagination={false}
            scroll={{ y: 500 }}
         />
      </>
   );
}
